import { createTheme } from "@mui/material/styles";
import { createContext, useMemo, useState } from "react";

// color design tokens
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#040509",
          200: "#080b12",
          300: "#0c101b",
          400: "#f2f0f0",
          500: "#141b2d",
          600: "#434957",
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
      }),
});

// mui theme settings
const FONT_FAMILY = ['"Public Sans"', "sans-serif"].join(",");

const CARD_SHADOW =
  "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px";

const DIVIDER = "rgba(145, 158, 171, 0.24)";

export const themeSettings = (mode) => {
  const colors = tokens(mode);

  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
            },
          }
        : {
            primary: {
              main: "#212b36",
              contrastText: "#ffffff",
            },
            secondary: {
              main: "#00a76f",
              contrastText: "#ffffff",
            },
            success: { main: "#22c55e", dark: "#118d57", light: "#77ed8b" },
            warning: { main: "#ffab00", dark: "#b76e00", light: "#ffd666" },
            error: { main: "#ff5630", dark: "#b71d18", light: "#ffac82" },
            info: { main: "#00b8d9", dark: "#006c9c", light: "#61f3f3" },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            text: {
              primary: "#212b36",
              secondary: "#637381",
              disabled: "#919eab",
            },
            divider: DIVIDER,
            background: {
              default: "#fcfcfc",
              paper: "#ffffff",
            },
          }),
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      h1: { fontSize: 32, fontWeight: 700, lineHeight: 1.25 },
      // Page titles
      h2: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.3,
        "@media (min-width:900px)": { fontSize: 28 },
      },
      h3: { fontSize: 22, fontWeight: 700, lineHeight: 1.35 },
      // Card titles
      h4: { fontSize: 18, fontWeight: 700, lineHeight: 1.4 },
      h5: { fontSize: 16, fontWeight: 600, lineHeight: 1.5 },
      h6: { fontSize: 14, fontWeight: 600, lineHeight: 1.5 },
      body1: { fontSize: 15, lineHeight: 1.5 },
      body2: { fontSize: 13, lineHeight: 1.55 },
      caption: { fontSize: 12 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600, textTransform: "none" },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            minHeight: 48,
            color: "#637381",
            "&.Mui-selected": { color: "#212b36" },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 16, boxShadow: CARD_SHADOW },
        },
      },
      MuiCardHeader: {
        defaultProps: {
          titleTypographyProps: { variant: "h4" },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 16 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "#212b36",
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            padding: "6px 10px",
          },
          arrow: { color: "#212b36" },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: { fontSize: 13 },
          columnHeaderTitle: { fontWeight: 600 },
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return [theme, colorMode];
};

export const colorGrid = {
  default: {
    text: "rgb(255, 255, 255)",
    background: "rgb(33, 43, 54)",
  },
  green: {
    text: "rgb(17, 141, 87)",
    background: "rgba(34, 197, 94, 0.16)",
  },
  blue: {
    text: "rgb(0, 108, 156)",
    background: "rgba(0, 184, 217, 0.16)",
  },
  yellow: {
    text: "rgb(183, 110, 0)",
    background: "rgba(255, 171, 0, 0.16)",
  },
  gray: {
    text: "rgb(99, 115, 129)",
    background: "rgba(145, 158, 171, 0.16)",
  },
  red: {
    background: "rgba(255, 86, 48, 0.16)",
    text: "rgb(183, 29, 24)",
  },
};


export const chartColors = [
  "#f2c6b4", // Soft Peach
  "#a8d5e2", // Soft Cyan
  "#f4a1c0", // Soft Pink
  "#d1c4e9", // Soft Purple
  "#ffe0b2", // Soft Amber
  "#a5d6a7", // Soft Green
  "#bbdefb", // Soft Light Blue
  "#f0e68c", // Soft Khaki
  "#c8e6c9", // Soft Mint
  "#d7ccc8", // Soft Taupe
  "#c5cae9", // Soft Periwinkle
]